
interface ExperienceDescriptionProps {
  position: string;
  company: string;
  startDate: string;
  endDate?: string | null;
  current?: boolean;
  skills?: string[];
}

export async function generateExperienceDescription({
  position,
  company,
  startDate,
  endDate,
  current = false,
  skills = []
}: ExperienceDescriptionProps): Promise<string> {
  try {
    // Format the dates for better readability
    const start = formatDate(startDate);
    const end = current ? 'Present' : endDate ? formatDate(endDate || '') : '';
    
    // Calculate duration
    const duration = calculateDuration(startDate, endDate, current);
    
    // Get position-based templates
    const templates = getPositionTemplates(position, company, duration, skills);
    
    // Select a random template
    const selectedTemplate = templates[Math.floor(Math.random() * templates.length)];
    
    return selectedTemplate;
  } catch (error) {
    console.error('Error generating experience description:', error);
    return `Worked at ${company} as a ${position}.`;
  }
}

// Format date from YYYY-MM to Month Year
function formatDate(dateString: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString + '-01'); // Add day to make it a valid date
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch {
    return dateString;
  }
}

// Calculate the duration between start and end dates
function calculateDuration(startDate: string, endDate?: string | null, current: boolean = false): string {
  if (!startDate) return '';
  
  try {
    const start = new Date(startDate + '-01');
    const end = current ? new Date() : endDate ? new Date(endDate + '-01') : new Date();
    
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    if (years === 0) {
      return months === 1 ? '1 month' : `${months} months`;
    } else if (years === 1 && months === 0) {
      return '1 year';
    } else if (months === 0) {
      return `${years} years`;
    } else {
      return `${years} years, ${months} months`;
    }
  } catch {
    return '';
  }
}

// Get position-specific templates
function getPositionTemplates(position: string, company: string, duration: string, skills: string[] = []): string[] {
  // Extract key terms from the position to determine the type
  const positionLower = position.toLowerCase();
  
  // Get the top 3 skills or fewer if less are available
  const topSkills = skills.slice(0, 3);
  const skillsText = topSkills.length > 0 
    ? `Leveraged expertise in ${topSkills.join(', ')}.` 
    : '';
  
  // Base templates that work for any position
  const baseTemplates = [
    `Served as ${position} at ${company} for ${duration}. Responsible for delivering high-quality work and collaborating with cross-functional teams. ${skillsText}`,
    
    `Worked at ${company} as a ${position} for ${duration}, contributing to various projects and initiatives. Helped drive business outcomes through creative problem-solving. ${skillsText}`,
    
    `${duration} experience as ${position} at ${company}. Collaborated with stakeholders to understand requirements and deliver solutions that met business needs. ${skillsText}`
  ];
  
  // Design-specific templates
  if (positionLower.includes('design') || positionLower.includes('designer') || positionLower.includes('ux') || positionLower.includes('ui')) {
    return [
      `Led design initiatives as ${position} at ${company} for ${duration}. Created visually appealing and user-centered designs that enhanced brand identity and user experience. ${skillsText}`,
      
      `Worked as a ${position} at ${company} for ${duration}, developing creative solutions for various projects. Collaborated with stakeholders to deliver designs that balanced aesthetics and functionality. ${skillsText}`,
      
      `${duration} experience as ${position} at ${company}. Responsible for creating and implementing design systems, conducting user research, and iterating on designs based on feedback. ${skillsText}`,
      
      ...baseTemplates
    ];
  }
  
  // Development/Engineering templates
  if (positionLower.includes('develop') || positionLower.includes('engineer') || positionLower.includes('programming') || positionLower.includes('code')) {
    return [
      `Served as ${position} at ${company} for ${duration}. Developed and maintained applications, collaborated on architecture decisions, and implemented best practices for code quality. ${skillsText}`,
      
      `${duration} experience as ${position} at ${company}. Built and optimized applications, fixed bugs, and contributed to technical documentation. Worked in an agile environment to deliver features on schedule. ${skillsText}`,
      
      `Worked as a ${position} at ${company} for ${duration}. Responsible for implementing new features, troubleshooting issues, and participating in code reviews to ensure high-quality deliverables. ${skillsText}`,
      
      ...baseTemplates
    ];
  }
  
  // Management templates
  if (positionLower.includes('manager') || positionLower.includes('director') || positionLower.includes('lead') || positionLower.includes('chief')) {
    return [
      `Led teams as ${position} at ${company} for ${duration}. Responsible for strategic planning, team development, and delivering projects on time and within budget. ${skillsText}`,
      
      `${duration} experience as ${position} at ${company}. Oversaw operations, mentored team members, and collaborated with stakeholders to align projects with business objectives. ${skillsText}`,
      
      `Served as ${position} at ${company} for ${duration}. Managed resources effectively, implemented process improvements, and drove innovation within the organization. ${skillsText}`,
      
      ...baseTemplates
    ];
  }
  
  // Marketing templates
  if (positionLower.includes('market') || positionLower.includes('brand') || positionLower.includes('content') || positionLower.includes('social media')) {
    return [
      `Worked as ${position} at ${company} for ${duration}. Developed and executed marketing strategies to increase brand awareness and drive engagement. ${skillsText}`,
      
      `${duration} experience as ${position} at ${company}. Created compelling content, managed campaigns, and analyzed metrics to optimize marketing efforts. ${skillsText}`,
      
      `Served as ${position} at ${company} for ${duration}. Collaborated with cross-functional teams to create cohesive marketing materials that resonated with target audiences. ${skillsText}`,
      
      ...baseTemplates
    ];
  }
  
  // Default to base templates if no specific category is matched
  return baseTemplates;
}
